import { PhoneForwarded } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/base/buttons'
import {
  useRedirectStatus,
  useDeactivateRedirect,
} from '@/hooks/api/useWhatsAppSignup'

function formatRemaining(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

interface VerificationRedirectBannerProps {
  assistantId: number
  /** Render the Cancel-redirect button. Default false (informative-only). */
  showCancel?: boolean
}

export function VerificationRedirectBanner({ assistantId, showCancel = false }: VerificationRedirectBannerProps) {
  const { data: redirectStatus } = useRedirectStatus(assistantId)
  const deactivateRedirect = useDeactivateRedirect()

  if (!redirectStatus?.active) return null

  return (
    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/50 dark:bg-amber-900/10">
      <div className="flex items-start gap-2">
        <PhoneForwarded className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
        <div className="flex-1 text-xs text-gray-800 dark:text-gray-200">
          <p className="font-medium">Verification redirect active</p>
          <p className="mt-1">
            Incoming calls to this assistant, coming from UK (Facebook), are temporarily redirected to{' '}
            <span className="font-mono">{redirectStatus.redirect_target}</span> for the next{' '}
            <span className="font-mono">{formatRemaining(redirectStatus.remaining_seconds)}</span>.
          </p>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Calls from any other country are unaffected.
          </p>
        </div>
        {showCancel && (
          <Button
            type="button"
            color="tertiary"
            size="sm"
            onPress={() => deactivateRedirect.mutate(
              { assistant_id: assistantId },
              {
                onSuccess: () => toast.success('Redirect cancelled — incoming UK calls are no longer forwarded'),
                onError: (e) => toast.error(`Cancel failed: ${e instanceof Error ? e.message : 'unknown'}`),
              },
            )}
            isLoading={deactivateRedirect.isPending}
          >
            Cancel redirect
          </Button>
        )}
      </div>
    </div>
  )
}
